import { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { DecoupledEditor } from 'ckeditor5';

import { ScreenSizes } from 'constants/sizes';

import Button from 'modules/_shared/components/Buttons';

import { setModal } from 'modules/_shared/redux/modalActions';
import { Council, Document } from 'types';
import { generateCouncilTemplates } from 'modules/administratives/utils/generateCouncilTemplates';
import { State } from 'redux/initialState';
import { saveCouncilAnnouncement } from 'modules/administratives/utils/saveCouncilAnnouncement';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import CKEditor from 'modules/_shared/components/CKEditor';
import PDFViewer from 'modules/_shared/components/PDFViewer';
import UploadDocumentsSection from 'modules/documents/components/UploadDocumentsSection';
import { addCouncilDocuments } from 'modules/administratives/utils/addCouncilDocuments';

type EditCouncilAnnouncementProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilAnnouncement: FC<EditCouncilAnnouncementProps> = ({
  council,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  // State for CKEditor instance
  const [isLoading, setIsLoading] = useState(false);
  const [editorInstance, setEditorInstance] = useState<DecoupledEditor | null>(
    null
  );
  const [editorContent, setEditorContent] = useState('');
  const [hideEmailTemplate, setHideEmailTemplate] = useState(false);

  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<
    string | null
  >(null);

  const handlePreviewDocument = (index: number) => {
    const currentDocument = attachments[index];
    if (currentDocument) {
      const url = URL.createObjectURL(currentDocument);
      setAttachmentPreviewUrl(url);
    } else {
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
      setAttachmentPreviewUrl(null);
    }
  };

  // Auto-preview the first document when attachments change
  useEffect(() => {
    if (attachments.length > 0) {
      // Clean up previous preview URL
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
      // Preview the first document
      const firstDocument = attachments[0];
      const url = URL.createObjectURL(firstDocument);
      setAttachmentPreviewUrl(url);
    } else if (attachmentPreviewUrl) {
      // Clean up preview URL when no attachments
      URL.revokeObjectURL(attachmentPreviewUrl);
      setAttachmentPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const handleEditorChange = () => {
    if (editorInstance) {
      const content = editorInstance.getData();
      setEditorContent(content);
    }
  };

  const handleClose = () => {
    if (attachmentPreviewUrl) {
      URL.revokeObjectURL(attachmentPreviewUrl);
    }
    dispatch(setModal(null));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const templates = await generateCouncilTemplates({ user, society });
      const { announcementTemplate } = templates;

      const announcementDocumentId = await saveCouncilAnnouncement({
        user,
        society,
        council,
        announcementTemplate,
      });

      // Upload attachments if any
      let uploadedAttachments: Document[] = [];
      if (attachments.length > 0) {
        uploadedAttachments = await addCouncilDocuments({
          files: attachments,
          user,
          society,
        });
      }

      const newCouncilData = {
        _id: council._id,
        announcementText: editorContent,
        announcementTemplate,
        announcementDocument: announcementDocumentId
          ? announcementTemplate['_id']
          : null,
        // Add uploaded attachments to public documents
        publicDocuments: [
          ...(council.publicDocuments || []),
          ...uploadedAttachments.map((doc) => doc._id),
        ],
      };

      await store.dispatch(updateCouncil(newCouncilData));
      await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('Announcement')}</span>
          {/* <small className="d-block fs-14px text-soft mt-1">
            {t('LaterCanModify')}
          </small> */}
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal gy-4">
          <div className="row">
            {/* Left side - Editor and Preview */}
            <div className="col-md-6">
              <div className="mb-4">
                <h5 className="nk-block-title mb-0">{t('EmailContent')}</h5>
              </div>
              <div
                className="editor-compact-version"
                style={{ width: '100%', height: '400px !important' }}
              >
                <CKEditor
                  editorInstance={editorInstance}
                  setEditorInstance={setEditorInstance}
                  documentName="Announcement"
                  documentModel={editorContent}
                  enableMentions={false}
                  isReadOnly={false}
                  onChangeContent={handleEditorChange}
                  isCompactVersion
                />
              </div>

              {/* Checkbox to hide email template */}
              <div className="mt-5 pt-2 form-group">
                <div className="custom-control custom-control-md custom-checkbox">
                  <input
                    className="custom-control-input"
                    type="checkbox"
                    id="hideEmailTemplate"
                    checked={hideEmailTemplate}
                    onChange={(e) => setHideEmailTemplate(e.target.checked)}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="hideEmailTemplate"
                  >
                    {t(
                      'Remove the default announcement template and keep only my text'
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* Right side - Upload Documents and PDF Preview */}
            <div className="col-md-6">
              <UploadDocumentsSection
                title={t('Attachment')}
                initialDocumentsCount={0}
                documents={attachments as unknown as Document[]}
                setDocuments={setAttachments}
                onPreviewDocument={handlePreviewDocument}
                hideColumnHeaders
                hasPreview
              />

              {/* PDF Preview */}
              <div className="mt-3">
                <div
                  className="border rounded p-0 overflow-hidden"
                  style={{
                    height: '330px',
                    overflow: 'auto',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  {attachmentPreviewUrl ? (
                    <PDFViewer
                      file={attachmentPreviewUrl}
                      width={400}
                      height={300}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: '#6c757d' }}
                    >
                      <div className="text-center">
                        <em
                          className="icon ni ni-file-pdf"
                          style={{ fontSize: '3rem' }}
                        />
                        <p className="mt-2 mb-0">{t('NoFileSelected')}</p>
                        <small>{t('UploadPDFToPreview')}</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLoading && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilAnnouncement;
